import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@aws-amplify/ui-react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';
import './App.css';

function App() {
  const [sessionId, setSessionId] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('sessionId');
    const ret = params.get('returnUrl');
    const accessKeyId = params.get('accessKeyId');
    const secretAccessKey = params.get('secretAccessKey');
    const sessionToken = params.get('sessionToken');

    if (!sid) {
      setError('Missing session ID. Please start verification from the ticketing app.');
      setLoading(false);
      return;
    }

    if (!ret) {
      setError('Missing return URL. Please start verification from the ticketing app.');
      setLoading(false);
      return;
    }

    if (!accessKeyId || !secretAccessKey || !sessionToken) {
      setError('Missing credentials. Please start verification from the ticketing app.');
      setLoading(false);
      return;
    }

    setSessionId(sid);
    setReturnUrl(ret);
    setCredentials({ accessKeyId, secretAccessKey, sessionToken });
    setLoading(false);

    // Clear URL params from address bar for security (credentials shouldn't stay visible)
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const handleAnalysisComplete = async () => {
    // Liveness challenge completed - redirect back with success
    const redirectUrl = buildReturnUrl(returnUrl, {
      livenessComplete: 'true',
      status: 'SUCCEEDED',
      sessionId: sessionId
    });

    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 1500);
  };

  const handleError = (livenessError) => {
    console.error('Liveness error:', livenessError);

    const redirectUrl = buildReturnUrl(returnUrl, {
      livenessComplete: 'true',
      status: 'ERROR',
      sessionId: sessionId,
      error: livenessError?.state || 'UNKNOWN_ERROR'
    });

    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 2000);
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="status-message">
          <div className="spinner"></div>
          <p>Initializing face verification...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="error-message">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          {returnUrl && (
            <button onClick={() => window.location.href = returnUrl} className="btn-return">
              Return to Ticketing App
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="app-container">
        <div className="liveness-wrapper">
          <FaceLivenessDetector
            sessionId={sessionId}
            region="eu-west-1"
            onAnalysisComplete={handleAnalysisComplete}
            onError={handleError}
            disableStartScreen={false}
            config={{
              credentialProvider: async () => credentials
            }}
          />
        </div>
      </div>
    </ThemeProvider>
  );
}

/**
 * Build return URL with query parameters appended
 */
function buildReturnUrl(baseUrl, params) {
  try {
    const url = new URL(baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  } catch (e) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    const queryString = Object.entries(params)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return baseUrl + separator + queryString;
  }
}

export default App;
